import dayjs from 'dayjs';
import { MyThermostat } from '../types';

export const observationMetricKeys = [
  'temperature',
  'humidity',
  'setpoint',
  'heatSetpoint',
  'coolSetpoint',
  'operatingMode',
  'operatingState',
  'fanMode',
  'fanSpeed',
  'fanState',
] as const;
export type ObservationMetricKey = (typeof observationMetricKeys)[number];

// Explicit null means verification is unavailable, never legacy receipt time.
// Omitted fields retain compatibility with older unrelated widget fixtures.
export function usesObservationEvidence(
  device: MyThermostat | undefined,
): boolean {
  return (
    device?.supportsObservationFreshness === true ||
    device?.supportsObservationFreshness === null
  );
}

export const unavailableObservationScope = 'Scope verification unavailable';

function validObservation(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value > 0 &&
    Number.isFinite(new Date(value).getTime())
  );
}

export function getMetricObservedAt(
  device: MyThermostat,
  key: ObservationMetricKey,
): number | undefined {
  if (!device.deviceMetrics?.some((metric) => metric?.key === key))
    return undefined;
  const value = device[key]?.observedAt;
  return validObservation(value) ? value : undefined;
}

export function getThermostatObservedAt(
  device: MyThermostat,
): number | undefined {
  let latest: number | undefined;
  for (const key of observationMetricKeys) {
    const value = getMetricObservedAt(device, key);
    if (value !== undefined && (latest === undefined || value > latest))
      latest = value;
  }
  return latest;
}

export function formatObservationAge(
  observedAt: number | undefined,
  nowMs: number,
): string {
  if (!validObservation(observedAt)) return 'No confirmed reading';
  return dayjs(observedAt).from(dayjs(nowMs));
}

export function formatObservationEvidence(
  observedAt: number | undefined,
  nowMs: number,
): string {
  const age = formatObservationAge(observedAt, nowMs);
  return validObservation(observedAt)
    ? `${age} (${new Date(observedAt).toISOString()})`
    : age;
}
